
import Vue from 'vue';
import _keyBy from 'lodash/keyBy';
import pluralize from 'pluralize';
import { mapActions, mapGetters, mapState } from 'vuex';
import { CompanyUserRoleDef } from '@run-diffusion/shared';
import { auth, db } from '@/firebase';
import { FiltersPanelMixin, JustAddedRowsMixin, get$bindFirestoreOptions, TeamUserMixin } from '@/mixins';
import { JUST_ADDED_LIST } from '@/utils/justAdded';
import { ROUTER } from '@/router/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { scimProvisioningMembershipTooltip } from '@/constants/scimProvisioning';
import { titleCase } from '@/filters';
import { StoredLastPagination } from '@/store';
import {
	MEMBERS_API_BASE_URL,
	MemberRow,
	MembersFetchFailure,
	MembersFetchResult,
	fetchMembersPage,
	membersFetchFailure,
} from '@/utils/membersApi';
import {
	MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
	MEMBERS_DIRECTORY_HEADER_VALUE,
	MEMBERS_DIRECTORY_ITEMS_PER_PAGE_OPTIONS,
	MIRROR_LAG_REFETCH_MS,
	MemberCompanyRoleReport,
	MemberCompanyRoleReports,
	MemberDownloadRow,
	MembersDirectoryTableOptions,
	MembersDirectoryWalk,
	MEMBERS_RESTING_FILTERS,
	MembersPanelFilters,
	HydratedMembersDirectoryView,
	TeamFilterItem,
	bulkCompanyRoleTargetFromRow,
	buildMembersRequest,
	canWalkBack,
	canWalkForward,
	fetchFailureKeepsRows,
	hasActiveMembersFilters,
	hydrateMembersDirectoryView,
	keepMemberCompanyRoleReports,
	memberDownloadRow,
	membersFetchFailureMessage,
	mergeTeamFilterItems,
	rememberMembersDirectoryView,
	restartWalk,
	WalkFooterPagination,
	walkAfterPage,
	walkBack,
	walkFooterPageText,
	walkFooterPagination,
	walkForward,
	walkPageNumber,
} from './membersDirectoryState';
import {
	BULK_COMPANY_ROLE_MODE,
	BulkCompanyRoleMode,
} from '@/views/Team/CompanyUserRoleDialogs/companyUserRoleDialogState';
import BaseBreadcrumbs from '@/components/base/BaseBreadcrumbs.vue';
import TableBulkActionsPopover from '@/components/base/TableBulkActionsPopover.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import FiltersPanel from '@/components/Filters/FiltersPanel.vue';
import TableToolbar from '@/components/Filters/TableToolbar.vue';
import MemberDirectoryRow from './MemberDirectoryRow.vue';
import { COMPANY_DIRECTORY_TITLE } from '@/views/Team/CompanyNav/companyNavState';

const DEFAULT_OPTIONS: MembersDirectoryTableOptions = {
	itemsPerPage: MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
	sortBy: [MEMBERS_DIRECTORY_HEADER_VALUE.EMAIL],
	sortDesc: [false],
};

/**
 * The All Team Members directory. Postgres (through api2) decides which rows
 * exist and in what order; every row then binds itself to Firestore and stays
 * live.
 *
 * Cursor paged, like every other api2 listing, which is why the table's own
 * footer is off and the same footer, fed from the walk, takes its place: the
 * endpoint can say "there is more" but not "there are 48", and counting the
 * company on every request to say so is not worth it. `walk` is the position,
 * and it is remembered with the filters and sort that produced it.
 */
export default Vue.extend({
	name: 'MembersDirectoryPage',
	mixins: [
		TeamUserMixin,
		FiltersPanelMixin,
		JustAddedRowsMixin,
	],
	metaInfo () {
		return {
			title: `${this.directoryTitle} | ${this.team?.company?.name || this.team?.name || titleCase(this.displayLabels.team)} | RunDiffusion`,
		};
	},
	data () {
		return {
			options: { ...DEFAULT_OPTIONS } as MembersDirectoryTableOptions,
			itemsPerPageOptions: MEMBERS_DIRECTORY_ITEMS_PER_PAGE_OPTIONS,
			didHydrateLastPagination: false,
			searchQuery: '',
			statusFilter: null as string | null,
			companyRoleFilter: null as string | null,
			teamFilter: null as string | null,
			// Every team the endpoint has named to this viewer so far. The
			// directory never binds teams/{id}, so this is the picker's source.
			teamFilterItems: [] as TeamFilterItem[],

			rows: [] as MemberRow[],
			// Where the walk is. Every filter, sort or page-size change restarts
			// it, because a cursor belongs to one listing and the endpoint
			// refuses it in any other.
			walk: restartWalk() as MembersDirectoryWalk,
			// The walk and page size that produced the rows on screen. The footer
			// describes those rows, so while the next page loads it keeps saying
			// "1-25 of many" instead of numbering a page that has not arrived.
			shownPage: {
				walk: restartWalk(),
				itemsPerPage: MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
			} as { walk: MembersDirectoryWalk; itemsPerPage: number },
			loading: false,
			failed: false,
			// Bumped on every request so a slow, superseded response is dropped
			// instead of overwriting a newer page.
			requestVersion: 0,
			fetchTimer: null as number | null,
			// True from a user-initiated fetch (page, sort, filter) until a fetch
			// completes. A background refetch that supersedes one answers for it.
			foregroundFetchPending: false,
			// The one pending catch-up refetch after a mutation, if any.
			mirrorLagRefetchTimer: null as number | null,
			// Set on the way out. TeamPage's dialogs outlive this page and hold
			// its onSuccess callbacks, so one can still be called afterwards.
			isDestroyed: false,

			companyUserRoleDefs: [] as CompanyUserRoleDef[],
			loadingCompanyUserRoleDefs: false,

			// Kept across pages, like the Team Members table's; a new search or filter clears it.
			selectedRows: [] as MemberRow[],
			// Each row's company role as it last reported it, by row key: the bulk actions and the CSV
			// see no cells. Kept for the rows on screen and the ones still selected.
			companyRoleByKey: {} as MemberCompanyRoleReports,
			// The CSV is a list of people, in address order whatever the table is sorted by.
			downloadSort: { sortBy: [MEMBERS_DIRECTORY_HEADER_VALUE.EMAIL], sortDesc: [false] },
			BULK_COMPANY_ROLE_MODE,
		};
	},
	computed: {
		...mapState([
			'team',
			'companyDashboard',
		]),
		...mapGetters([
			'displayLabels',
		]),
		companyId (): string | null {
			return this.team?.companyId || null;
		},
		// JustAddedRowsMixin: an invitation a company role created here shows on top before the listing has it
		justAddedListKey (): string | null {
			return this.companyId ? JUST_ADDED_LIST.companyMembers(this.companyId) : null;
		},
		scimMembershipTooltip (): string {
			return scimProvisioningMembershipTooltip(this.displayLabels);
		},
		// Its name in the company nav, its crumb and its tab title alike
		directoryTitle (): string {
			return COMPANY_DIRECTORY_TITLE;
		},
		companyUserRoleDefsMap (): Record<string, CompanyUserRoleDef> {
			return _keyBy(this.companyUserRoleDefs || [], 'id');
		},
		// Who may change company roles in bulk: the page-level Assign's people, and never under SCIM.
		canManageCompanyRoles (): boolean {
			return !!(this.hasManageCompanyPermission && !this.scimProvisioningLocksMembership);
		},
		// Who sees company roles at all: the people the page-level Assign Company Role is shown to.
		// Anyone else gets no Company Role column, no company role in the CSV, and no role filter.
		showCompanyRoles (): boolean {
			return !!this.hasManageCompanyPermission;
		},
		// The role filter applies only where it can be seen. One remembered from an earlier visit is
		// set aside for anyone else, never applied out of sight.
		appliedCompanyRoleFilter (): string | null {
			return this.showCompanyRoles ? this.companyRoleFilter : null;
		},
		// The role definitions name the column's badges and fill the filter, so only a viewer who sees
		// company roles reads them.
		companyRoleDefsCompanyId (): string | null {
			return this.showCompanyRoles ? this.companyId : null;
		},
		downloadRows (): MemberDownloadRow[] {
			return this.selectedRows.map((row: MemberRow) => {
				const report: MemberCompanyRoleReport | undefined = this.companyRoleByKey[row.key];
				const roleDef: CompanyUserRoleDef | undefined = report && report.companyUserRoleDefId
					? this.companyUserRoleDefsMap[report.companyUserRoleDefId]
					: undefined;
				return memberDownloadRow(row, roleDef ? roleDef.name : null);
			});
		},
		// The popover turns anything but letters and digits into dashes: "Acme Inc" saves as Acme-Inc-Directory.csv
		downloadFileName (): string {
			const companyName: string = this.team?.company?.name || this.team?.name || '';
			return companyName ? `${companyName}-${this.directoryTitle}` : this.directoryTitle;
		},
		canWalkBack (): boolean {
			return canWalkBack(this.walk);
		},
		footerPagination (): WalkFooterPagination {
			return walkFooterPagination(this.shownPage.walk, this.shownPage.itemsPerPage, this.rows.length);
		},
		// The footer reads the page and the page size, and hands back a copy with
		// one of them changed. The size is the one picked, so the select shows it at once.
		footerOptions (): MembersDirectoryTableOptions & { page: number } {
			return {
				...this.options,
				page: this.footerPagination.page,
				itemsPerPage: this.options.itemsPerPage || MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
			};
		},
		footerPageText (): string {
			return walkFooterPageText(this.shownPage.walk, this.footerPagination);
		},
		// What the sort watcher compares, so an unchanged sort arriving as a new
		// object does not restart the walk.
		sortSignature (): string {
			return JSON.stringify([this.options.sortBy, this.options.sortDesc]);
		},
		// Where the first crumb and the subtitle's link both go
		companyDashboardRoute () {
			return {
				name: ROUTER.COMPANY_DASHBOARD,
				query: this.$route.query,
			};
		},
		breadcrumbs () {
			return [
				{
					hoverColor: 'var(--primary-purple)',
					text: 'Company Dashboard',
					disabled: false,
					to: this.companyDashboardRoute,
				},
				{
					hoverColor: 'var(--primary-blue)',
					text: this.directoryTitle,
					disabled: true,
					to: {
						name: ROUTER.MEMBERS_DIRECTORY,
						query: this.$route.query,
					},
				},
			];
		},
		headers () {
			return [
				{
					text: 'Email',
					value: MEMBERS_DIRECTORY_HEADER_VALUE.EMAIL,
					align: 'left',
					class: 'py-0 pl-0 pr-1',
					sortable: true,
				},
				{
					text: titleCase(pluralize(this.displayLabels.team)),
					value: MEMBERS_DIRECTORY_HEADER_VALUE.TEAMS,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
					// Shown in the table, left out of the CSV
					noDownload: true,
				},
				// The CSV takes its columns from these, so a viewer without the column has none there either
				...(this.showCompanyRoles ? [
					{
						text: 'Company Role',
						value: MEMBERS_DIRECTORY_HEADER_VALUE.COMPANY_ROLE,
						align: 'left',
						class: 'py-0 px-1',
						sortable: false,
					},
				] : []),
				{
					text: 'Joined',
					value: MEMBERS_DIRECTORY_HEADER_VALUE.JOINED,
					align: 'left',
					class: 'py-0 px-1',
					sortable: true,
				},
				{
					text: 'Actions',
					value: MEMBERS_DIRECTORY_HEADER_VALUE.ACTIONS,
					align: 'right',
					class: 'py-0 pr-4',
					sortable: false,
					noDownload: true,
				},
			];
		},
		statusFilterItems () {
			return [
				{ text: 'Joined', value: 'joined' },
				{ text: 'Pending', value: 'pending' },
			];
		},
		// FiltersPanelMixin: the box at rest, and the three filters the listing was fetched with
		restingPanelFilters (): MembersPanelFilters {
			return MEMBERS_RESTING_FILTERS;
		},
		appliedPanelFilters (): MembersPanelFilters {
			return {
				statusFilter: this.statusFilter,
				companyRoleFilter: this.appliedCompanyRoleFilter,
				teamFilter: this.teamFilter,
			};
		},
		teamFilterPlaceholder (): string {
			return titleCase(this.displayLabels.team);
		},
		teamFilterNoDataText (): string {
			return `No ${pluralize(this.displayLabels.team)} to filter by`;
		},
		hasActiveFilters (): boolean {
			return hasActiveMembersFilters({
				searchQuery: this.searchQuery,
				statusFilter: this.statusFilter,
				companyRoleFilter: this.appliedCompanyRoleFilter,
				teamFilter: this.teamFilter,
			});
		},
		emptyStateMessage (): string {
			if (this.failed) return 'Could not load members';
			if (this.hasActiveFilters) return 'No members match your search or filters';
			return `No ${this.displayLabels.team} members yet`;
		},
	},
	watch: {
		companyId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal === oldVal) return;
				// Another company's teams are not this one's to filter by, nor its people to act on.
				this.teamFilter = null;
				this.teamFilterItems = [];
				this.clearSelectedRows();
				this.companyRoleByKey = {};
				this.hydrateLastPagination(newVal);
				this.scheduleFetch();
			},
		},
		companyRoleDefsCompanyId: {
			immediate: true,
			handler (newVal: string | null, oldVal: string | null) {
				if (newVal === oldVal) return;
				this.bindCompanyUserRoleDefs(newVal);
			},
		},
		// The permission can arrive after the page does (it loads with the viewer's company role),
		// and a role can be granted or taken while the page is open. A remembered role filter starts
		// or stops applying then, so the box and the listing follow it.
		showCompanyRoles (newVal: boolean, oldVal: boolean) {
			if (newVal === oldVal || !this.companyRoleFilter) return;
			this.syncFiltersPanel();
			this.resetToFirstPage();
		},
		/**
		 * The table owns the sort, and changing it changes the listing, so the
		 * walk restarts. Watched on the sort alone rather than deep on
		 * `options`: the page size has its own handler that restarts too, and a
		 * deep watcher fired on every echo of the object.
		 */
		sortSignature (newVal: string, oldVal: string) {
			if (newVal === oldVal) return;
			this.resetToFirstPage();
		},
	},
	created () {
		this.scheduleFetch();
	},
	beforeDestroy () {
		this.isDestroyed = true;
		if (this.fetchTimer !== null) {
			window.clearTimeout(this.fetchTimer);
			this.fetchTimer = null;
		}
		this.clearMirrorLagRefetch();
		if (this.companyId) {
			// The filters travel with the page: a remembered page is only
			// meaningful against the result set that produced it.
			this.storeLastPagination({
				key: 'lastMembersDirectoryPagination',
				pagination: rememberMembersDirectoryView(this.options, {
					searchQuery: this.searchQuery,
					statusFilter: this.statusFilter,
					companyRoleFilter: this.companyRoleFilter,
					teamFilter: this.teamFilter,
				}, this.walk.cursor, walkPageNumber(this.walk)),
				companyId: this.companyId,
			});
		}
	},
	methods: {
		// JustAddedRowsMixin: a person's row is keyed by their address
		justAddedRowId (row: MemberRow): string {
			return row.key;
		},
		...mapActions([
			'updateSnackbar',
			'storeLastPagination',
		]),
		hydrateLastPagination (companyId: string | null) {
			if (this.didHydrateLastPagination || !companyId) return;
			const stored: StoredLastPagination | undefined = this.companyDashboard?.lastMembersDirectoryPagination?.[companyId];
			if (!stored) return;
			this.didHydrateLastPagination = true;
			const view: HydratedMembersDirectoryView = hydrateMembersDirectoryView(stored, this.options);
			this.options = view.options;
			this.walk = view.walk;
			if (view.filters) {
				this.searchQuery = view.filters.searchQuery;
				this.statusFilter = view.filters.statusFilter;
				this.companyRoleFilter = view.filters.companyRoleFilter;
				this.teamFilter = view.filters.teamFilter;
				// The box starts closed; the Filter button counts what is restored.
				this.syncFiltersPanel();
			}
		},
		/**
		 * Coalesce the bursts the table produces (its own mount-time emit, a
		 * page reset followed by the echoed options) into one request. The
		 * version guard in fetchPage covers anything that still overlaps.
		 */
		scheduleFetch () {
			if (this.fetchTimer !== null) return;
			this.fetchTimer = window.setTimeout(() => {
				this.fetchTimer = null;
				this.fetchPage();
			}, 0);
		},
		refetch (): Promise<void> {
			return this.fetchPage();
		},
		/**
		 * Someone added, or a row found removed, changes who is listed, and that
		 * can only catch up once the mirror has the write, which an immediate
		 * refetch would precede. An edit asks for nothing: the rows follow it
		 * through their bindings and stay where they are until Refresh. At most
		 * one timer is pending: a repeat call restarts it, so the refetch that
		 * fires is a full mirror lag after the latest change.
		 */
		refetchAfterMirrorLag () {
			// A dialog on TeamPage can finish after this page is gone (browser
			// back while it is open). A timer armed then would fetch, and could
			// raise an error snackbar, on whatever page the user is on now.
			if (this.isDestroyed) return;
			this.clearMirrorLagRefetch();
			this.mirrorLagRefetchTimer = window.setTimeout(() => {
				this.mirrorLagRefetchTimer = null;
				if (this.isDestroyed) return;
				this.fetchPage(true);
			}, MIRROR_LAG_REFETCH_MS);
		},
		clearMirrorLagRefetch () {
			if (this.mirrorLagRefetchTimer === null) return;
			window.clearTimeout(this.mirrorLagRefetchTimer);
			this.mirrorLagRefetchTimer = null;
		},
		/**
		 * A row's events, passed up to TeamPage (which owns the dialogs), with the
		 * onSuccess a dialog there calls on completion when there is one. A row's own
		 * change asks for nothing again: the rows follow it live where they are, and
		 * someone it removes is marked so by their own row (onRowRemoved).
		 */
		reemitToTeamPage (eventName: string, payload: Record<string, unknown>, onSuccess?: () => void) {
			this.$emit(eventName, onSuccess ? { ...payload, onSuccess } : payload);
		},
		// A row's person is no longer listed (off every Team, with no company role this page shows):
		// never act on them, and ask again once the mirror knows.
		onRowRemoved (key: string) {
			if (!key) return;
			this.selectedRows = this.selectedRows.filter((row: MemberRow) => row.key !== key);
			this.forgetJustAddedRow(key);
			this.refetchAfterMirrorLag();
		},
		clearSelectedRows () {
			this.selectedRows = [];
		},
		onRowCompanyRole ({ key, companyUserRoleDefId, hasCompanyRole }: { key: string } & MemberCompanyRoleReport) {
			if (!key) return;
			this.companyRoleByKey = {
				...this.companyRoleByKey,
				[key]: { companyUserRoleDefId, hasCompanyRole },
			};
		},
		/**
		 * The bulk Assign or Remove Company Role, for TeamPage's bulk dialog. The selection
		 * clears once something changed. The rows follow it live where they are, and anyone
		 * it takes out of the listing is marked so by their own row.
		 */
		onBulkCompanyRole (mode: BulkCompanyRoleMode) {
			if (!this.selectedRows.length) return;
			this.$emit('bulk-company-user-role', {
				mode,
				targets: this.selectedRows.map((row: MemberRow) => bulkCompanyRoleTargetFromRow(row, this.companyRoleByKey[row.key])),
				onSuccess: () => this.clearSelectedRows(),
			});
		},
		/**
		 * The page-level Add: no row, so TeamPage's dialog takes any address and
		 * asks which team to invite it to should it have no account yet. An
		 * invitation adds a person: on top at once (JustAddedRowsMixin), and in the
		 * listing once the mirror has them.
		 */
		onAddCompanyUserRole () {
			this.reemitToTeamPage('add-company-user-role', {
				row: null,
				email: '',
				userId: null,
				companyUserRoleDefId: null,
			}, () => this.refetchAfterMirrorLag());
		},
		/**
		 * `background` is the catch-up refetch nobody asked for: if it fails the
		 * table keeps the rows it has (they are live through their bindings)
		 * and only the snackbar says so. Unless it superseded a fetch the user
		 * did ask for: then it answers for that one, and fails as it would have.
		 */
		async fetchPage (background: boolean = false): Promise<void> {
			const companyId: string | null = this.companyId;
			if (!companyId) {
				// Nothing to ask for. Supersede any request still in flight, or
				// the previous company's people would land under no company.
				this.requestVersion++;
				this.foregroundFetchPending = false;
				this.loading = false;
				this.failed = false;
				this.walk = restartWalk();
				this.showRows([]);
				return;
			}

			const version: number = ++this.requestVersion;
			if (!background) this.foregroundFetchPending = true;
			this.loading = true;
			this.failed = false;

			let result: MembersFetchResult;
			try {
				const currentUser = auth.currentUser;
				const idToken: string | null = currentUser ? await currentUser.getIdToken() : null;
				if (!idToken) {
					result = { ok: false, reason: 'unauthorized' };
				} else {
					result = await fetchMembersPage({
						baseUrl: MEMBERS_API_BASE_URL,
						idToken,
						request: buildMembersRequest({
							companyId,
							accountTeamId: this.team?.id || null,
							cursor: this.walk.cursor,
							options: this.options,
							searchQuery: this.searchQuery,
							statusFilter: this.statusFilter,
							companyRoleFilter: this.appliedCompanyRoleFilter,
							teamFilter: this.teamFilter,
						}),
					});
				}
			} catch (e) {
				console.error('Error loading members directory:', e);
				result = { ok: false, reason: 'failed' };
			}

			// A newer request superseded this one; let it own the table. And a
			// page that is gone owns nothing: a failure landing now would
			// raise its snackbar on whatever page the user is on instead.
			if (this.isDestroyed || version !== this.requestVersion) return;

			this.loading = false;
			const supersededForeground: boolean = this.foregroundFetchPending;
			this.foregroundFetchPending = false;
			if (result.ok) {
				this.walk = walkAfterPage(this.walk, result.page);
				this.showRows(result.page.rows);
				this.teamFilterItems = mergeTeamFilterItems(this.teamFilterItems, result.page.rows);
				return;
			}

			// The position we resumed from is not in this listing any more, which
			// a remembered cursor can become. Start the walk over rather than
			// telling someone about a cursor they never saw.
			const failure: MembersFetchFailure | null = membersFetchFailure(result);
			if (failure && failure.reason === 'stale-cursor' && this.walk.cursor) {
				this.walk = restartWalk();
				this.scheduleFetch();
				return;
			}

			if (!fetchFailureKeepsRows({ background, supersededForeground, hasRows: !!this.rows.length })) {
				this.failed = true;
				this.walk = restartWalk();
				this.showRows([]);
			}
			this.notifyFetchFailure(result);
		},
		// Rows go on screen together with the walk and page size that produced them.
		showRows (rows: MemberRow[]) {
			this.rows = rows;
			this.shownPage = {
				walk: this.walk,
				itemsPerPage: this.options.itemsPerPage || MEMBERS_DIRECTORY_DEFAULT_ITEMS_PER_PAGE,
			};
			// A selected person back on screen is selected as this copy, so the CSV reads the newer one.
			const shownByKey: Record<string, MemberRow> = _keyBy(rows, 'key');
			if (this.selectedRows.some((row: MemberRow) => shownByKey[row.key] && shownByKey[row.key] !== row)) {
				this.selectedRows = this.selectedRows.map((row: MemberRow) => shownByKey[row.key] || row);
			}
			this.companyRoleByKey = keepMemberCompanyRoleReports(this.companyRoleByKey, [
				...rows.map((row: MemberRow) => row.key),
				...this.selectedRows.map((row: MemberRow) => row.key),
			]);
		},
		notifyFetchFailure (result: MembersFetchResult) {
			const failure: MembersFetchFailure | null = membersFetchFailure(result);
			if (!failure) return;
			// No company selected yet: nothing to show and nothing to complain about.
			if (failure.code === 'COMPANY_REQUIRED') return;

			const messageByReason: Record<string, string> = {
				unauthorized: 'Your session has expired, sign in again to view members',
				forbidden: 'You do not have access to this directory',
				network: 'Could not reach the server, check your connection and try again',
				failed: 'Could not load members, please try again',
			};
			this.updateSnackbar({
				status: SNACKBAR_STATUS.ERROR,
				message: membersFetchFailureMessage(result, messageByReason),
				show: true,
			});
		},
		/**
		 * Back to the start of the walk, and fetch. Every filter change does
		 * this: a cursor is a position in one listing, and the endpoint refuses
		 * it in another, so there is nothing to carry over.
		 */
		resetToFirstPage () {
			this.walk = restartWalk();
			this.scheduleFetch();
		},
		setItemsPerPage (itemsPerPage: number) {
			// Restart too. The page size does not invalidate a cursor, but
			// resuming mid-walk with a different size shows a page that starts
			// in an arbitrary place, which reads as rows going missing.
			this.options = { ...this.options, itemsPerPage };
			this.resetToFirstPage();
		},
		goToNextPage () {
			if (!canWalkForward(this.walk)) return;
			this.walk = walkForward(this.walk);
			this.scheduleFetch();
		},
		goToPreviousPage () {
			if (!canWalkBack(this.walk)) return;
			this.walk = walkBack(this.walk);
			this.scheduleFetch();
		},
		/**
		 * The footer hands back its options with one thing changed: the page
		 * size (a new walk from the top), or the page, one either way. A page
		 * restored from the store has no cursors behind it, so its Back starts
		 * the walk over rather than doing nothing.
		 */
		onFooterOptions (next: { page?: number; itemsPerPage?: number }) {
			if (next.itemsPerPage && next.itemsPerPage !== this.footerOptions.itemsPerPage) {
				this.setItemsPerPage(next.itemsPerPage);
				return;
			}
			if (!next.page || next.page === this.footerOptions.page) return;
			if (next.page > this.footerOptions.page) {
				this.goToNextPage();
			} else if (canWalkBack(this.walk)) {
				this.goToPreviousPage();
			} else {
				this.resetToFirstPage();
			}
		},
		setSearchQuery (query: string) {
			const next: string = query || '';
			if (next !== this.searchQuery) this.clearSelectedRows();
			this.searchQuery = next;
			this.resetToFirstPage();
		},
		// FiltersPanelMixin: Apply and Clear all land here, once something changed
		commitPanelFilters (next: MembersPanelFilters) {
			this.clearSelectedRows();
			this.statusFilter = next.statusFilter;
			this.companyRoleFilter = next.companyRoleFilter;
			this.teamFilter = next.teamFilter;
			this.resetToFirstPage();
		},
		async bindCompanyUserRoleDefs (companyId: string | null) {
			if (!companyId) {
				if (this.$firestoreRefs?.companyUserRoleDefs) this.$unbind('companyUserRoleDefs');
				this.companyUserRoleDefs = [];
				return;
			}

			this.loadingCompanyUserRoleDefs = true;
			try {
				// index created: companyUserRoleDefs: companyId Ascending isDeleted Ascending createdAt Descending __name__ Descending
				const roleDefsRef = db
					.collection('companyUserRoleDefs')
					.where('companyId', '==', companyId)
					.where('isDeleted', '==', false)
					.orderBy('createdAt', 'desc');
				await this.$bind(
					'companyUserRoleDefs',
					roleDefsRef,
					get$bindFirestoreOptions(),
				);
			} catch (error) {
				console.error('Error binding company user role defs:', error);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Failed to load company roles',
					show: true,
				});
			} finally {
				this.loadingCompanyUserRoleDefs = false;
			}
		},
	},
	components: {
		BaseBreadcrumbs,
		EmptyState,
		FiltersPanel,
		MemberDirectoryRow,
		TableBulkActionsPopover,
		TableToolbar,
	},
});
